// Setting the plugins up: each plugin's `setup(app)` runs once, in a fixed
// order, with an `app` object of its own, all of them before the first
// render. A plugin whose setup throws (or whose returned promise rejects) is
// reported and skipped; the others still set up, as Piral did with a failing
// pilet. An async setup is not waited for: what it registers later shows up
// as it registers, since every slot follows the registry.

export interface PluginModule {
  setup?(app: unknown): unknown;
}

export interface PluginEntry {
  /** The plugin's own folder (under src/plugins/<feature>/), which fixes the order. */
  folder: string;
  /** The package name: the app's `meta.name`. */
  name: string;
  version: string;
  module: PluginModule;
}

export interface LoadResult {
  /** Plugins whose setup returned (or whose async setup has resolved so far). */
  loaded: string[];
  failed: Array<{ name: string; error: unknown }>;
  /** Resolves when every async setup has settled. */
  settled: Promise<void>;
}

/** Calls every plugin's setup now, in folder order. */
export function setupPlugins(
  entries: PluginEntry[],
  createApi: (meta: { name: string; version: string }) => unknown,
  report: (message: string, error: unknown) => void = (message, error) => console.error(message, error),
): LoadResult {
  const result: LoadResult = { loaded: [], failed: [], settled: Promise.resolve() };
  const pending: Array<Promise<void>> = [];
  for (const entry of [...entries].sort((a, b) => a.folder.localeCompare(b.folder))) {
    const fail = (error: unknown) => {
      report(`[${entry.name}] setup failed`, error);
      result.failed.push({ name: entry.name, error });
    };
    if (typeof entry.module.setup !== 'function') {
      fail(new Error('the plugin exports no setup function'));
      continue;
    }
    try {
      const out = entry.module.setup(createApi({ name: entry.name, version: entry.version }));
      if (out && typeof (out as Promise<unknown>).then === 'function') {
        pending.push((out as Promise<unknown>).then(() => void result.loaded.push(entry.name), fail));
      } else {
        result.loaded.push(entry.name);
      }
    } catch (error) {
      fail(error);
    }
  }
  result.settled = Promise.all(pending).then(() => undefined);
  return result;
}

/**
 * Pairs the entry modules found under src/plugins/ with their package.json, by
 * folder: `../../../plugins/<feature>/<folder>/src/index.tsx` with
 * `../../../plugins/<feature>/<folder>/package.json`. The folder is the
 * plugin's own, not its feature's, so the setup order does not depend on how
 * plugins are grouped.
 */
export function pairPlugins(
  modules: Record<string, PluginModule>,
  packages: Record<string, { name?: string; version?: string }>,
): PluginEntry[] {
  const folderOf = (path: string) => /([^/]+)\/(?:src\/index\.tsx|package\.json)$/.exec(path)?.[1];
  const pkgByFolder = new Map(Object.entries(packages).map(([path, pkg]) => [folderOf(path), pkg]));
  return Object.entries(modules).flatMap(([path, module]) => {
    const folder = folderOf(path);
    if (!folder) return [];
    const pkg = pkgByFolder.get(folder) ?? {};
    return [{ folder, name: pkg.name ?? folder, version: pkg.version ?? '0.0.0', module }];
  });
}
